import { Platform } from 'react-native';
import type RNCallKeepType from 'react-native-callkeep';
import { callEvents } from './callEvents';

/**
 * Native call integration (iOS CallKit / Android ConnectionService) via
 * react-native-callkeep, for WhatsApp-style outbound calls:
 *  - iOS: the call shows in the Phone app "Recents" and gets the native call UI.
 *  - Android: native call treatment (audio focus, in-call notification, lock
 *    screen) — no system call-log entry (matches WhatsApp on Android).
 *
 * Pairs with LiveKit: `registerGlobals({ autoConfigureAudioSession: false })`
 * (see [index.js]) hands the iOS audio session to CallKit, and we relay
 * CallKit's audio-session activation to WebRTC via `RTCAudioSession`.
 *
 * Native-only and **import-safe**: `react-native-callkeep` accesses native
 * modules at load (it builds a `NativeEventEmitter`, which throws on Android
 * when the native module isn't in the build). We therefore lazy-`require` it
 * inside a guard so a dev client that hasn't been rebuilt with the native module
 * degrades to plain LiveKit calling instead of crashing the whole app.
 */

const APP_NAME = 'Nomadas Chat';

let cachedModule: typeof RNCallKeepType | null = null;
let moduleLoaded = false;

/**
 * Lazily load react-native-callkeep; returns null when it can't be used.
 *
 * **iOS only.** react-native-callkeep 4.3.x is incompatible with the React
 * Native New Architecture on Android: its native module exports a duplicate
 * `displayIncomingCall` @ReactMethod, which the TurboModule interop rejects
 * ("Module exports two methods to JavaScript with the same name") the moment JS
 * touches `NativeModules.RNCallKeep`. Android also can't surface VoIP calls in
 * the system call log, so on Android we skip callkeep entirely and rely on plain
 * LiveKit calling. (If Android ConnectionService is ever needed, it requires a
 * patched callkeep — see CLAUDE.md.)
 */
function getCallKeep(): typeof RNCallKeepType | null {
    if (moduleLoaded) return cachedModule;
    moduleLoaded = true;
    if (Platform.OS !== 'ios') return cachedModule; // null on Android / web
    try {
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        cachedModule = require('react-native-callkeep').default ?? null;
    } catch (e) {
        console.log('react-native-callkeep is unavailable — native call treatment is disabled.', e);
        cachedModule = null;
    }
    return cachedModule;
}

/** Relay CallKit's audio-session events to WebRTC (iOS). Lazy + guarded. */
function notifyWebRtcAudio(active: boolean): void {
    try {
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        const { RTCAudioSession } = require('@livekit/react-native-webrtc');
        if (active) RTCAudioSession.audioSessionDidActivate();
        else RTCAudioSession.audioSessionDidDeactivate();
    } catch (e) {
        console.log('RTCAudioSession relay error:', e);
    }
}

let setupPromise: Promise<void> | null = null;

/**
 * Registers the app with CallKit / ConnectionService and wires the global,
 * app-lifetime listeners. Idempotent — runs once. No-op if callkeep is missing.
 * Call at the native root.
 */
export function setupCallKeep(): Promise<void> {
    if (Platform.OS === 'web') return Promise.resolve();
    if (setupPromise) return setupPromise;

    const RNCallKeep = getCallKeep();
    if (!RNCallKeep) {
        setupPromise = Promise.resolve();
        return setupPromise;
    }

    setupPromise = (async () => {
        await RNCallKeep.setup({
            ios: {
                appName: APP_NAME,
                supportsVideo: false,
                maximumCallGroups: '1',
                maximumCallsPerCallGroup: '1',
                // Show the call in the iOS Phone app "Recents" (WhatsApp behaviour).
                includesCallsInRecents: true,
            },
            android: {
                alertTitle: 'Phone account permission',
                alertDescription: 'Nomadas Chat needs permission to manage in-app calls.',
                cancelButton: 'Cancel',
                okButton: 'OK',
                additionalPermissions: [],
                selfManaged: false,
                foregroundService: {
                    channelId: 'com.nomadas.universechat.call',
                    channelName: 'Ongoing calls',
                    notificationTitle: `${APP_NAME} call in progress`,
                },
            },
        });

        // CallKit owns the iOS AVAudioSession; relay its activation to WebRTC so
        // LiveKit can use the mic/speakers. No-op on Android.
        RNCallKeep.addEventListener('didActivateAudioSession', () => {
            if (Platform.OS === 'ios') notifyWebRtcAudio(true);
        });
        RNCallKeep.addEventListener('didDeactivateAudioSession', () => {
            if (Platform.OS === 'ios') notifyWebRtcAudio(false);
        });

        // User acted in the system call UI → forward to the mounted call screen.
        RNCallKeep.addEventListener('endCall', ({ callUUID }) => {
            callEvents.emit('endCall', { callUUID });
        });
        RNCallKeep.addEventListener('didPerformSetMutedCallAction', ({ callUUID, muted }) => {
            callEvents.emit('setMuted', { callUUID, muted });
        });
    })().catch((e) => {
        setupPromise = null; // allow a later retry if setup failed
        console.log('CallKeep setup error:', e);
    });

    return setupPromise;
}

/** Start an outbound native call. Pass the contact's phone number when known. */
export async function startNativeCall(opts: {
    uuid: string;
    handle: string;
    name: string;
    isPhoneNumber: boolean;
}): Promise<void> {
    const RNCallKeep = getCallKeep();
    if (!RNCallKeep) return;
    await setupCallKeep();
    RNCallKeep.startCall(
        opts.uuid,
        opts.handle,
        opts.name,
        opts.isPhoneNumber ? 'number' : 'generic',
        false,
    );
    // iOS: mark the outgoing call as connecting (shows the pending Recents entry).
    if (Platform.OS === 'ios') {
        RNCallKeep.reportConnectingOutgoingCallWithUUID(opts.uuid);
    }
}

/** Mark the call connected — on iOS this is what writes it to Phone ▸ Recents. */
export function reportNativeCallConnected(uuid: string): void {
    const RNCallKeep = getCallKeep();
    if (!RNCallKeep) return;
    if (Platform.OS === 'ios') {
        RNCallKeep.reportConnectedOutgoingCallWithUUID(uuid);
    } else if (Platform.OS === 'android') {
        RNCallKeep.setCurrentCallActive(uuid);
    }
}

/** Tear down the native call (clears the system call UI). */
export function endNativeCall(uuid: string): void {
    getCallKeep()?.endCall(uuid);
}

/** Reflect the in-app mute state in the CallKit UI (iOS only). */
export function setNativeMuted(uuid: string, muted: boolean): void {
    if (Platform.OS === 'ios') getCallKeep()?.setMutedCall(uuid, muted);
}

/** RFC-4122 v4 UUID (lowercase). CallKit only needs per-call uniqueness. */
export function newCallUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}
